/**
 * Preview storage utilities using sessionStorage
 * For non-logged-in users to demo flashcard features
 */

import type { FlashcardImport } from './flashcardSchemas'
import { calculateAnkiSchedule } from './ankiAlgorithm'

export interface PreviewFlashcard {
  id: string
  deckId: string
  frontSpanish: string[]
  backEnglish: string[]
  cardType: 'word' | 'phrase' | 'lyrics'
  targetVerb?: string
  sourceVerb?: string
  easeFactor: number
  interval: number
  repetitions: number
  dueDate: number
  lastReviewDate?: number
  priority: number
  tags?: string[]
  notes?: string
  isActive: boolean
  isLearned: boolean
  isArchived: boolean
  createdAt: number
  updatedAt: number
}

export interface PreviewDeck {
  id: string
  name: string
  description?: string
  createdAt: number
  updatedAt: number
}

const STORAGE_KEYS = {
  DECKS: 'preview_flashcard_decks',
  FLASHCARDS: 'preview_flashcards',
  REVIEWS: 'preview_flashcard_reviews',
} as const

/**
 * Initialize default preview data (15 verbs, 25 adjectives)
 * This is a placeholder - actual initialization happens in route components
 */
export function initializePreviewData() {
  // Check if already initialized
  if (sessionStorage.getItem(STORAGE_KEYS.DECKS)) {
    return
  }
  // Initialization handled by route components that have access to JSON imports
}

// Helper functions removed - initialization handled in route component

/**
 * Get all preview decks
 */
export function getPreviewDecks(): PreviewDeck[] {
  const data = sessionStorage.getItem(STORAGE_KEYS.DECKS)
  return data ? JSON.parse(data) : []
}

/**
 * Get flashcards for a deck
 */
export function getPreviewFlashcards(deckId?: string): PreviewFlashcard[] {
  const data = sessionStorage.getItem(STORAGE_KEYS.FLASHCARDS)
  const flashcards: PreviewFlashcard[] = data ? JSON.parse(data) : []
  if (deckId) {
    return flashcards.filter((f) => f.deckId === deckId)
  }
  return flashcards
}

/**
 * Get due flashcards
 */
export function getDuePreviewFlashcards(limit?: number): PreviewFlashcard[] {
  const flashcards = getPreviewFlashcards()
  const now = Date.now()
  const due = flashcards
    .filter((f) => f.isActive && !f.isArchived && f.dueDate <= now)
    .sort((a, b) => a.dueDate - b.dueDate)
  return limit ? due.slice(0, limit) : due
}

/**
 * Submit a review for a preview flashcard
 */
export function submitPreviewReview(flashcardId: string, grade: 0 | 1 | 2 | 3 | 4 | 5): void {
  const flashcards = getPreviewFlashcards()
  const flashcard = flashcards.find((f) => f.id === flashcardId)
  if (!flashcard) return

  const newSchedule = calculateAnkiSchedule(
    grade,
    flashcard.easeFactor,
    flashcard.interval,
    flashcard.repetitions
  )

  flashcard.easeFactor = newSchedule.easeFactor
  flashcard.interval = newSchedule.interval
  flashcard.repetitions = newSchedule.repetitions
  flashcard.dueDate = newSchedule.dueDate
  flashcard.lastReviewDate = Date.now()
  flashcard.isLearned = newSchedule.interval > 90
  flashcard.updatedAt = Date.now()

  // Update in storage
  const index = flashcards.findIndex((f) => f.id === flashcardId)
  flashcards[index] = flashcard
  sessionStorage.setItem(STORAGE_KEYS.FLASHCARDS, JSON.stringify(flashcards))

  // Save review history
  const reviews = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.REVIEWS) || '[]')
  reviews.push({
    flashcardId,
    grade,
    reviewedAt: Date.now(),
    beforeEaseFactor: flashcard.easeFactor,
    afterEaseFactor: newSchedule.easeFactor,
    beforeInterval: flashcard.interval,
    afterInterval: newSchedule.interval,
    beforeRepetitions: flashcard.repetitions,
    afterRepetitions: newSchedule.repetitions,
  })
  sessionStorage.setItem(STORAGE_KEYS.REVIEWS, JSON.stringify(reviews))
}

/**
 * Create a preview deck
 */
export function createPreviewDeck(name: string, description?: string): PreviewDeck {
  const decks = getPreviewDecks()
  const newDeck: PreviewDeck = {
    id: `preview-deck-${Date.now()}`,
    name,
    description,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  }
  decks.push(newDeck)
  sessionStorage.setItem(STORAGE_KEYS.DECKS, JSON.stringify(decks))
  return newDeck
}

/**
 * Import flashcards to preview deck
 */
export function importPreviewFlashcards(
  deckId: string,
  flashcards: FlashcardImport[]
): PreviewFlashcard[] {
  const existing = getPreviewFlashcards()
  const now = Date.now()
  const newFlashcards: PreviewFlashcard[] = flashcards.map((card, idx) => ({
    id: `preview-card-${Date.now()}-${idx}`,
    deckId,
    frontSpanish: card.frontSpanish,
    backEnglish: card.backEnglish,
    cardType: card.cardType || 'lyrics',
    targetVerb: card.targetVerb,
    sourceVerb: card.sourceVerb,
    easeFactor: 2.5,
    interval: 0,
    repetitions: 0,
    dueDate: now,
    priority: card.priority ?? 5,
    tags: card.tags,
    notes: card.notes,
    isActive: true,
    isLearned: false,
    isArchived: false,
    createdAt: now,
    updatedAt: now,
  }))

  const allFlashcards = [...existing, ...newFlashcards]
  sessionStorage.setItem(STORAGE_KEYS.FLASHCARDS, JSON.stringify(allFlashcards))
  return newFlashcards
}

/**
 * Create a preview flashcard manually
 */
export function createPreviewFlashcardManually(
  deckId: string,
  frontSpanish: string[],
  backEnglish: string[],
  cardType: 'word' | 'phrase' | 'lyrics',
  targetVerb?: string,
  sourceVerb?: string,
  priority?: number
): PreviewFlashcard {
  const flashcards = getPreviewFlashcards()
  const now = Date.now()
  const newCard: PreviewFlashcard = {
    id: `preview-card-${Date.now()}`,
    deckId,
    frontSpanish,
    backEnglish,
    cardType,
    targetVerb,
    sourceVerb,
    easeFactor: 2.5,
    interval: 0,
    repetitions: 0,
    dueDate: now,
    priority: priority ?? 5,
    isActive: true,
    isLearned: false,
    isArchived: false,
    createdAt: now,
    updatedAt: now,
  }

  flashcards.push(newCard)
  sessionStorage.setItem(STORAGE_KEYS.FLASHCARDS, JSON.stringify(flashcards))
  return newCard
}

